import React, { memo, useCallback, useState } from "react";
import { HStack, VStack, Text, Button, Spacer, Box } from "@chakra-ui/react";
import { useInterval } from "react-use";
import colors from "app/colors";
import { APP_UPDATE_PROMPT_Z_INDEX } from "modules/providers/chakra-provider/chakra-portal-stack";
import { useVideoCallProvider } from "modules/providers/video-call-provider";

const nextLocalMidnight = () => {
  const midnight = new Date();
  midnight.setHours(24, 0, 0, 0);
  return midnight.getTime();
};

const AppUpdateListener = () => {
  const TIMEOUT_MS = 1000 * 60 * 2;
  const [id, setId] = useState<string>();

  const [display, setDisplay] = useState(false);
  const [refreshDeadline, setRefreshDeadline] = useState<number>();

  const { isCallActive } = useVideoCallProvider();

  const onRefreshClicked = useCallback(() => {
    window.location.reload();
  }, []);

  useInterval(() => {
    // deadline check runs on the poll interval (not a one-shot timer) so it
    // survives background-tab throttling and laptop sleep across midnight
    if (
      refreshDeadline != null &&
      Date.now() >= refreshDeadline &&
      !isCallActive
    ) {
      window.location.reload();
      return;
    }

    fetch("ver.txt", { cache: "no-store" })
      .then((response) => {
        if (response.ok) {
          return response.text().then((newId) => {
            if (!id) {
              setId(newId);
              return;
            }
            if (id !== newId) {
              setDisplay(true);
              setRefreshDeadline((current) => current ?? nextLocalMidnight());
            }
          });
        }
        return undefined;
      })
      .catch(() => {
        // the poll runs while offline/asleep too; a failed check just waits
        // for the next interval
      });
  }, TIMEOUT_MS);

  if (!display) return null;

  return (
    <Box
      pos="absolute"
      bottom={10}
      left="50%"
      transform="translateX(-50%);"
      zIndex={APP_UPDATE_PROMPT_Z_INDEX}
    >
      <HStack
        backgroundColor={colors.blackTertiary}
        width={350}
        borderRadius={7}
        p={2}
      >
        <VStack align="flex-start" spacing={0}>
          <Text color={colors.white}>New App Version Available</Text>
          <Text color={colors.white} fontSize="xs" opacity={0.7}>
            The app will refresh automatically at midnight
          </Text>
        </VStack>
        <Spacer />
        <Button onClick={onRefreshClicked} variant="ghost" color={colors.white}>
          Refresh
        </Button>
      </HStack>
    </Box>
  );
};

export default memo(AppUpdateListener);
